import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, FileDown } from "lucide-react";
import { useSite } from "@/lib/useSite";
import { imgUrl } from "@/lib/api";
import Chapter from "@/components/Chapter";

const LAYOUT = [
  { span: "md:col-span-7", aspect: "aspect-[16/10]" },
  { span: "md:col-span-5", aspect: "aspect-[8/7]" },
  { span: "md:col-span-5", aspect: "aspect-[8/7]" },
  { span: "md:col-span-7", aspect: "aspect-[16/10]" },
  { span: "md:col-span-4", aspect: "aspect-[4/5]" },
  { span: "md:col-span-4", aspect: "aspect-[4/5]" },
  { span: "md:col-span-4", aspect: "aspect-[4/5]" },
  { span: "md:col-span-6", aspect: "aspect-[16/10]" },
  { span: "md:col-span-6", aspect: "aspect-[16/10]" },
];

export default function ProductsGrid() {
  const { data } = useSite();
  const products = data?.products || [];
  const catalog = data?.settings?.catalog_pdf;

  return (
    <div>
      <Chapter
        no="01"
        kicker="The Range"
        heading="Every material. One roof."
        sub="Nine categories. One yard. Zero compromise."
      />
      <div className="grid grid-cols-1 gap-x-8 gap-y-16 md:grid-cols-12">
        {products.map((p, i) => {
          const slot = LAYOUT[i % LAYOUT.length];
          return (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 44 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className={`col-span-1 ${slot.span}`}
            >
              <Link to={`/products/${p.slug}`} data-testid={`product-category-card-${i}`} className="group block">
                <div className={`overflow-hidden rounded-[1.75rem] border border-ink/10 bg-ink/[0.04] shadow-[0_18px_45px_-22px_rgba(29,29,31,0.25)] transition-shadow duration-500 group-hover:shadow-[0_32px_70px_-24px_rgba(29,29,31,0.42)] ${slot.aspect}`}>
                  <img
                    src={imgUrl(p.photos?.[0])}
                    alt={p.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                  />
                </div>
                <div className="mt-5 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <span className="font-mono text-[11px] tracking-[0.25em] text-srs">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="mt-1.5 font-display text-2xl font-medium tracking-tight text-ink sm:text-[1.65rem]">{p.title}</h3>
                    {p.tagline && (
                      <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">{p.tagline}</p>
                    )}
                  </div>
                  <span className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-ink/15 text-ink transition-all duration-300 group-hover:border-srs group-hover:bg-srs group-hover:text-white">
                    <ArrowUpRight size={16} />
                  </span>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>
      {catalog && (
        <div className="mt-16 flex justify-center">
          <a
            data-testid="catalog-download-button"
            href={imgUrl(catalog)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 rounded-full border border-ink px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-ink transition-colors hover:bg-ink hover:text-white"
          >
            <FileDown size={15} /> Download product catalog
          </a>
        </div>
      )}
    </div>
  );
}
